import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_URL } from "../config/api";
import {
  REWARD_WHEEL_CLOSE_HINT,
  REWARD_WHEEL_CLOSE_HINT_DELAY_MS,
  REWARD_WHEEL_SEGMENT_ANGLE,
  REWARD_WHEEL_SEGMENT_COUNT,
  REWARD_WHEEL_SPIN_DURATION_MS,
  REWARD_WHEEL_RESULT_REVEAL_MS,
  REWARD_WHEEL_TYPES,
  aggregateWheelPrizes,
  animateWheelRotation,
  computeWheelRotation,
  formatResolvedWheelPrize,
  getSegmentPolarPositionAtRotation,
  getWheelPrizeKey,
  getWheelTitle,
  hydrateWheelSegmentsFromApi,
} from "../constants/rewardWheel";
import "./RewardWheel.css";

const WHEEL_COLOR_DARK = "#270036";
const WHEEL_COLOR_LIGHT = "#7d0c98";

const WHEEL_FRAME_SIZE = 480;
const WHEEL_ROTOR_SIZE = 428;
const WHEEL_PRIZE_RADIUS = 172;
const WHEEL_FRAME_RADIUS = WHEEL_FRAME_SIZE / 2;
const WHEEL_LIGHT_RADIUS = WHEEL_FRAME_RADIUS - 6;

function waitMs(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Jeton unique par demande d'ouverture : le serveur ne débite qu'une potion par jeton. */
function createWheelOpenToken() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

function preloadWheelImage(url) {
  return Promise.race([
    new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        if (typeof image.decode === "function") {
          image.decode().then(resolve, resolve);
        } else {
          resolve();
        }
      };
      image.onerror = () => resolve();
      image.src = url;
    }),
    new Promise((resolve) => {
      window.setTimeout(resolve, 4000);
    }),
  ]);
}

function preloadCinzelFont() {
  if (typeof document === "undefined" || !document.fonts?.load) {
    return Promise.resolve();
  }

  return Promise.race([
    document.fonts.load("700 22px Cinzel"),
    new Promise((resolve) => {
      window.setTimeout(resolve, 1500);
    }),
  ]).catch(() => {});
}

function waitForWheelAssets(segments) {
  const urls = [
    ...new Set(
      (Array.isArray(segments) ? segments : [])
        .map((segment) => segment?.imageUrl)
        .filter(Boolean)
    ),
  ];

  return Promise.all([
    ...urls.map((url) => preloadWheelImage(url)),
    preloadCinzelFont(),
  ]);
}

function buildWheelGradient(segments) {
  const sliceCount = segments.length || REWARD_WHEEL_SEGMENT_COUNT;
  const stops = Array.from({ length: sliceCount }, (_, index) => {
    const start = index * REWARD_WHEEL_SEGMENT_ANGLE;
    const end = (index + 1) * REWARD_WHEEL_SEGMENT_ANGLE;
    const color = index % 2 === 0 ? WHEEL_COLOR_DARK : WHEEL_COLOR_LIGHT;
    return `${color} ${start}deg ${end}deg`;
  });
  return `conic-gradient(from 0deg, ${stops.join(", ")})`;
}

function buildSegmentHighlightGradient(segmentIndex) {
  const start = segmentIndex * REWARD_WHEEL_SEGMENT_ANGLE;
  const end = (segmentIndex + 1) * REWARD_WHEEL_SEGMENT_ANGLE;
  return `conic-gradient(from 0deg, transparent 0deg ${start}deg, rgba(255, 248, 180, 0.55) ${start}deg ${end}deg, transparent ${end}deg 360deg)`;
}

export default function RewardWheel({
  wheelType = REWARD_WHEEL_TYPES.ENCOURAGEMENT,
  cloverPotionId,
  stockQuantity = 1,
  onPrepared,
  onSpinComplete,
  onDismiss,
  onRegisterCancelRequest,
}) {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [preparingNext, setPreparingNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [segments, setSegments] = useState([]);
  const [activeWheelType, setActiveWheelType] = useState(wheelType);
  const [resultIndex, setResultIndex] = useState(null);
  const [resolvedPrize, setResolvedPrize] = useState(null);
  const [error, setError] = useState(null);
  const [spinsCompleted, setSpinsCompleted] = useState(0);
  const [sessionStock, setSessionStock] = useState(() =>
    Math.max(1, Number(stockQuantity) || 1)
  );
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchSummary, setBatchSummary] = useState(null);
  const [batchSummaryCancelled, setBatchSummaryCancelled] = useState(false);
  const [closeHintReady, setCloseHintReady] = useState(false);

  const rotationRef = useRef(0);
  const spinLockRef = useRef(false);
  const cancelRef = useRef(false);
  const sessionIdRef = useRef(null);
  const segmentsRef = useRef([]);
  const spinsCompletedRef = useRef(0);
  const batchTargetRef = useRef(1);
  const batchRunningRef = useRef(false);
  const batchPrizesRef = useRef([]);
  /** Potion pour laquelle le tirage d'ouverture a déjà été demandé au serveur. */
  const startedForPotionRef = useRef(null);
  /** Jeton d'ouverture : stable pour toute la durée de vie de cette fenêtre. */
  const openTokenRef = useRef(null);
  const onPreparedRef = useRef(onPrepared);

  useEffect(() => {
    onPreparedRef.current = onPrepared;
  }, [onPrepared]);

  useEffect(() => {
    spinsCompletedRef.current = spinsCompleted;
  }, [spinsCompleted]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  const prepareWheel = useCallback(
    async ({ showLoading = true, openToken = createWheelOpenToken() } = {}) => {
      if (!cloverPotionId) return false;

      if (showLoading) {
        setLoading(true);
      } else {
        setPreparingNext(true);
      }

      setError(null);
      setSessionId(null);
      sessionIdRef.current = null;
      setResultIndex(null);
      setResolvedPrize(null);
      setCloseHintReady(false);
      setRotation(0);
      rotationRef.current = 0;

      try {
        const response = await fetch(`${API_URL}/reward-wheel/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cloverPotionId, openToken }),
        });
        const json = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(json.message || "Impossible d'ouvrir la roue.");
        }

        setSessionId(json.sessionId);
        sessionIdRef.current = json.sessionId;
        setActiveWheelType(json.wheelType ?? wheelType);
        onPreparedRef.current?.(json);

        const nextSegments = hydrateWheelSegmentsFromApi(json.segments ?? []);
        await waitForWheelAssets(nextSegments);
        setSegments(nextSegments);
        segmentsRef.current = nextSegments;
        return true;
      } catch (prepareError) {
        setError(prepareError.message || "Impossible d'ouvrir la roue.");
        return false;
      } finally {
        if (showLoading) {
          setLoading(false);
        } else {
          setPreparingNext(false);
        }
      }
    },
    [cloverPotionId, wheelType]
  );

  useEffect(() => {
    if (!cloverPotionId) return undefined;

    // Le serveur consomme la potion dès la requête « start » : on ne doit
    // l'envoyer qu'une seule fois par ouverture, même si l'effet est rejoué
    // (StrictMode en développement, changement d'identité d'un callback…).
    if (startedForPotionRef.current === cloverPotionId) return undefined;
    startedForPotionRef.current = cloverPotionId;
    openTokenRef.current = createWheelOpenToken();

    const stock = Math.max(1, Number(stockQuantity) || 1);

    cancelRef.current = false;
    batchRunningRef.current = false;
    setBatchRunning(false);
    setBatchSummary(null);
    setBatchSummaryCancelled(false);
    batchPrizesRef.current = [];
    setSpinsCompleted(0);
    spinsCompletedRef.current = 0;
    setSessionStock(stock);
    batchTargetRef.current = 1;
    setError(null);
    setSessionId(null);
    setResultIndex(null);
    setResolvedPrize(null);
    setCloseHintReady(false);

    prepareWheel({ showLoading: true, openToken: openTokenRef.current });

    return undefined;
  }, [cloverPotionId, prepareWheel, stockQuantity]);

  const wheelGradient = useMemo(() => buildWheelGradient(segments), [segments]);
  const wheelTitle = closeHintReady
    ? REWARD_WHEEL_CLOSE_HINT
    : getWheelTitle(activeWheelType);

  const prizePositions = useMemo(
    () =>
      segments.map((_, index) =>
        getSegmentPolarPositionAtRotation(
          index,
          rotation,
          WHEEL_PRIZE_RADIUS,
          WHEEL_ROTOR_SIZE
        )
      ),
    [rotation, segments]
  );

  const skeletonPrizePositions = useMemo(
    () =>
      Array.from({ length: REWARD_WHEEL_SEGMENT_COUNT }, (_, index) =>
        getSegmentPolarPositionAtRotation(
          index,
          0,
          WHEEL_PRIZE_RADIUS,
          WHEEL_ROTOR_SIZE
        )
      ),
    []
  );

  const showSkeleton = (loading || preparingNext) && !error;
  const showTitleSkeleton = loading && !error;

  const showBatchButton =
    sessionStock > 1 &&
    spinsCompleted === 0 &&
    !batchRunning &&
    !spinning &&
    !preparingNext &&
    !loading &&
    Boolean(sessionId) &&
    !error;

  const canSpin =
    !loading &&
    !preparingNext &&
    !spinning &&
    !batchRunning &&
    Boolean(sessionId) &&
    !error &&
    spinsCompleted === 0;

  const finalizeBatch = useCallback(
    (cancelled = false) => {
      if (batchPrizesRef.current.length > 0) {
        setBatchSummary(aggregateWheelPrizes(batchPrizesRef.current));
        setBatchSummaryCancelled(cancelled);
        cancelRef.current = false;
        return true;
      }
      return false;
    },
    []
  );

  const spinOnce = useCallback(async () => {
    const activeSessionId = sessionIdRef.current;
    if (!activeSessionId) {
      throw new Error("Session de roue introuvable.");
    }

    setSpinning(true);
    setResultIndex(null);
    setResolvedPrize(null);
    setError(null);

    const response = await fetch(`${API_URL}/reward-wheel/spin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId: activeSessionId }),
    });
    const json = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(json.message || "Impossible de tourner la roue.");
    }

    const nextIndex = json.segmentIndex;
    const fromRotation = rotationRef.current;
    const toRotation = computeWheelRotation(nextIndex, fromRotation);

    await animateWheelRotation(
      fromRotation,
      toRotation,
      REWARD_WHEEL_SPIN_DURATION_MS,
      (currentRotation) => {
        rotationRef.current = currentRotation;
        setRotation(currentRotation);
      }
    );

    rotationRef.current = toRotation;
    setRotation(toRotation);
    setResultIndex(nextIndex);
    const prize = json.prize ?? segmentsRef.current[nextIndex] ?? null;
    setResolvedPrize(prize);

    const nextCompleted = spinsCompletedRef.current + 1;
    spinsCompletedRef.current = nextCompleted;
    setSpinsCompleted(nextCompleted);
    onSpinComplete?.(json);
    setSpinning(false);

    return json;
  }, [onSpinComplete]);

  const runBatch = useCallback(async () => {
    if (spinLockRef.current) return;
    spinLockRef.current = true;
    batchRunningRef.current = true;
    setBatchRunning(true);
    setBatchSummary(null);
    setBatchSummaryCancelled(false);
    batchPrizesRef.current = [];

    try {
      const target = batchTargetRef.current;

      while (spinsCompletedRef.current < target) {
        if (cancelRef.current) break;

        if (spinsCompletedRef.current > 0) {
          const prepared = await prepareWheel({ showLoading: false });
          if (!prepared || cancelRef.current) break;
        }

        const json = await spinOnce();
        const prize =
          json.prize ?? segmentsRef.current[json.segmentIndex] ?? null;
        if (prize) {
          batchPrizesRef.current.push(prize);
        }

        await waitMs(REWARD_WHEEL_RESULT_REVEAL_MS);

        if (cancelRef.current) break;
      }

      if (cancelRef.current) {
        if (!finalizeBatch(true)) {
          onDismiss?.({
            spinsCompleted: spinsCompletedRef.current,
            cancelled: true,
          });
        }
        return;
      }

      if (
        spinsCompletedRef.current >= target &&
        batchPrizesRef.current.length > 0
      ) {
        finalizeBatch(false);
      }
    } catch (spinError) {
      setError(spinError.message || "Impossible de tourner la roue.");
    } finally {
      batchRunningRef.current = false;
      setBatchRunning(false);
      spinLockRef.current = false;
    }
  }, [finalizeBatch, onDismiss, prepareWheel, spinOnce]);

  const handleSpin = useCallback(async () => {
    if (!canSpin || spinLockRef.current) return;

    spinLockRef.current = true;
    batchTargetRef.current = 1;
    try {
      await spinOnce();
    } catch (spinError) {
      setError(spinError.message || "Impossible de tourner la roue.");
    } finally {
      spinLockRef.current = false;
    }
  }, [canSpin, spinOnce]);

  const handleBatchStart = useCallback(() => {
    if (!showBatchButton || spinLockRef.current) return;
    batchTargetRef.current = sessionStock;
    runBatch();
  }, [runBatch, sessionStock, showBatchButton]);

  const handleCancelRequest = useCallback(() => {
    if (batchSummary) {
      onDismiss?.({
        spinsCompleted: spinsCompletedRef.current,
        cancelled: batchSummaryCancelled,
      });
      return;
    }

    if (batchRunning || spinning || preparingNext) {
      cancelRef.current = true;
      return;
    }

    onDismiss?.({
      spinsCompleted: spinsCompletedRef.current,
      cancelled: false,
    });
  }, [
    batchRunning,
    batchSummary,
    batchSummaryCancelled,
    onDismiss,
    preparingNext,
    spinning,
  ]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      handleCancelRequest();
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [handleCancelRequest]);

  useEffect(() => {
    onRegisterCancelRequest?.(handleCancelRequest);
    return () => onRegisterCancelRequest?.(null);
  }, [handleCancelRequest, onRegisterCancelRequest]);

  useEffect(() => {
    const prizeSettled =
      !loading &&
      !spinning &&
      !batchRunning &&
      !preparingNext &&
      ((Boolean(resolvedPrize) && spinsCompleted > 0) || Boolean(batchSummary));

    if (!prizeSettled) {
      setCloseHintReady(false);
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setCloseHintReady(true);
    }, REWARD_WHEEL_CLOSE_HINT_DELAY_MS);

    return () => window.clearTimeout(timerId);
  }, [
    batchRunning,
    batchSummary,
    loading,
    preparingNext,
    resolvedPrize,
    spinning,
    spinsCompleted,
  ]);

  const spinBtnClass = [
    "reward-wheel__spin-btn",
    canSpin ? "reward-wheel__spin-btn--pulse" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const resultText = resolvedPrize ? formatResolvedWheelPrize(resolvedPrize) : null;

  const resultMessage = (() => {
    if (batchSummary) {
      if (batchSummaryCancelled) {
        return `${spinsCompleted} tours effectués sur ${sessionStock}`;
      }
      return `${sessionStock} tours terminés`;
    }
    if (error) return error;
    if (batchRunning && preparingNext) {
      return (
        <>
          <span className="reward-wheel__result-main">
            Préparation du tour {spinsCompleted + 1} / {sessionStock}…
          </span>
          <span className="reward-wheel__result-escape">Échap pour arrêter</span>
        </>
      );
    }
    if (batchRunning && spinning) {
      return (
        <>
          <span className="reward-wheel__result-main">
            Tour {spinsCompleted + 1} / {sessionStock}…
          </span>
          <span className="reward-wheel__result-escape">Échap pour arrêter</span>
        </>
      );
    }
    if (resultText && batchRunning) {
      return (
        <>
          <span className="reward-wheel__result-main">
            Tour {spinsCompleted} / {sessionStock} — lot&nbsp;:{" "}
            <strong>{resultText}</strong>
          </span>
          <span className="reward-wheel__result-escape">Échap pour arrêter</span>
        </>
      );
    }
    if (resultText) {
      return (
        <>
          Lot gagné&nbsp;: <strong>{resultText}</strong>
        </>
      );
    }
    if (loading) {
      return (
        <span className="reward-wheel-skel reward-wheel-skel--hint" aria-hidden="true" />
      );
    }
    if (spinning || preparingNext) {
      return "\u00a0";
    }
    return "Appuyez sur Tourner pour lancer la roue.";
  })();

  const prizeRevealed = Boolean(resultText) && !spinning && !batchRunning;
  const showIdleHint =
    !batchSummary &&
    !error &&
    !batchRunning &&
    !resultText &&
    !loading &&
    !spinning &&
    !preparingNext;

  return (
    <div
      className={`reward-wheel${showSkeleton ? " reward-wheel--skeleton" : ""}`}
      aria-busy={showSkeleton || undefined}
    >
      <h2
        id="reward-wheel-modal-title"
        className={`reward-wheel__title${
          closeHintReady ? " reward-wheel__title--close-hint" : ""
        }${showTitleSkeleton ? " reward-wheel__title--skeleton" : ""}`}
      >
        {showTitleSkeleton ? (
          <span className="reward-wheel__title-slot">
            <span className="reward-wheel-skel-metrics">
              {getWheelTitle(activeWheelType)}
            </span>
            <span className="reward-wheel-skel reward-wheel-skel--title" />
          </span>
        ) : (
          wheelTitle
        )}
      </h2>

      <div className="reward-wheel__stage">
        {batchSummary ? (
          <div
            className="reward-wheel__batch-summary"
            style={{ "--wheel-size": `${WHEEL_FRAME_SIZE}px` }}
          >
            <div className="reward-wheel__batch-summary-scroll">
              <table className="reward-wheel__batch-summary-table">
                <tbody>
                  {batchSummary.map((item) => (
                    <tr key={getWheelPrizeKey(item)}>
                      <td className="reward-wheel__batch-summary-cell reward-wheel__batch-summary-cell--icon">
                        <img
                          src={item.imageUrl}
                          alt=""
                          className={`reward-wheel__batch-summary-image${
                            item.type === "potion"
                              ? " reward-wheel__batch-summary-image--potion"
                              : ""
                          }${
                            item.type === "key"
                              ? " reward-wheel__batch-summary-image--key"
                              : ""
                          }`}
                          decoding="sync"
                          draggable={false}
                        />
                      </td>
                      <td className="reward-wheel__batch-summary-cell reward-wheel__batch-summary-cell--count">
                        ×{item.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <>
            <div
              className="reward-wheel__frame"
              style={{ "--wheel-size": `${WHEEL_FRAME_SIZE}px` }}
            >
              <div
                className="reward-wheel__rotor"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  background: wheelGradient,
                }}
              >
                {resultIndex != null && (
                  <div
                    className="reward-wheel__segment-highlight"
                    style={{
                      background: buildSegmentHighlightGradient(resultIndex),
                    }}
                    aria-hidden="true"
                  />
                )}
              </div>

              <div className="reward-wheel__prizes">
                {showSkeleton
                  ? skeletonPrizePositions.map((position, index) => (
                      <div
                        key={`skel-prize-${index}`}
                        className="reward-wheel__segment-content"
                        style={{
                          left: `${position.left}%`,
                          top: `${position.top}%`,
                        }}
                      >
                        <div className="reward-wheel__prize-row">
                          <span className="reward-wheel-skel reward-wheel-skel--prize" />
                        </div>
                      </div>
                    ))
                  : segments.map((segment, index) => {
                      const position = prizePositions[index];
                      const showCount = segment.count > 1;

                      return (
                        <div
                          key={`${segment.id}-${index}`}
                          className="reward-wheel__segment-content"
                          style={{
                            left: `${position.left}%`,
                            top: `${position.top}%`,
                          }}
                        >
                          <div className="reward-wheel__prize-row">
                            {showCount && (
                              <span className="reward-wheel__prize-count">
                                {segment.count}
                              </span>
                            )}
                            <img
                              src={segment.imageUrl}
                              alt=""
                              className={`reward-wheel__prize-image${
                                segment.type === "potion"
                                  ? " reward-wheel__prize-image--potion"
                                  : ""
                              }${
                                segment.type === "key"
                                  ? " reward-wheel__prize-image--key"
                                  : ""
                              }`}
                              decoding="sync"
                              draggable={false}
                            />
                          </div>
                        </div>
                      );
                    })}
              </div>

              <div className="reward-wheel__rim" aria-hidden="true" />

              <div className="reward-wheel__lights" aria-hidden="true">
                {Array.from({ length: 20 }, (_, index) =>
                  index === 0 ? null : (
                    <span
                      key={`light-${index}`}
                      className="reward-wheel__light"
                      style={{
                        transform: `rotate(${index * 18}deg) translateY(-${WHEEL_LIGHT_RADIUS}px)`,
                        animationDelay: `${(index % 2) * 0.55}s`,
                      }}
                    />
                  )
                )}
              </div>

              <button
                type="button"
                className={`${spinBtnClass}${
                  showSkeleton ? " reward-wheel__spin-btn--skeleton" : ""
                }`}
                onClick={handleSpin}
                disabled={!canSpin || showSkeleton}
                aria-hidden={showSkeleton || undefined}
                tabIndex={showSkeleton ? -1 : undefined}
              >
                {showSkeleton || (!spinning && spinsCompleted > 0)
                  ? null
                  : spinning
                    ? "…"
                    : "Tourner"}
              </button>
            </div>

            <svg
              className="reward-wheel__pointer"
              viewBox="0 0 30 44"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="pointer-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffe066" />
                  <stop offset="50%" stopColor="#d4a017" />
                  <stop offset="100%" stopColor="#8a6412" />
                </linearGradient>
              </defs>
              <path
                d="M0 0 L30 0 L30 22 Q30 28 22 36 L17 42 Q15 44 13 42 L8 36 Q0 28 0 22 Z"
                fill="url(#pointer-grad)"
              />
            </svg>
          </>
        )}
      </div>

      <div className="reward-wheel__footer">
        <p
          className={`reward-wheel__result${
            showIdleHint ? " reward-wheel__result--hint" : ""
          }${
            prizeRevealed || batchSummary ? " reward-wheel__result--visible" : ""
          }${prizeRevealed || batchSummary ? " reward-wheel__result--prize" : ""}${
            error ? " reward-wheel__result--error" : ""
          }`}
          role="status"
          aria-live="polite"
        >
          {showIdleHint ? (
            <span className="reward-wheel__arrive-warning">
              Ne fermez pas cette fenêtre avant d&apos;avoir tourné la roue, la
              potion serait quand même consommée.
            </span>
          ) : null}
          <span className={showIdleHint ? "reward-wheel__idle-hint" : undefined}>
            {resultMessage}
          </span>
        </p>

        <div className="reward-wheel__actions">
          {showTitleSkeleton && sessionStock > 1 ? (
            <span className="reward-wheel-skel reward-wheel-skel--action" />
          ) : showBatchButton ? (
            <button
              type="button"
              className="reward-wheel__action-btn reward-wheel__action-btn--all"
              onClick={handleBatchStart}
            >
              Tourner {sessionStock} fois
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
